import { AccessibilityInfo, useWindowDimensions, type View } from "@-/com.npmjs.react-native";
import { useSharedValue } from "@-/com.npmjs.react-native-reanimated";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import useLatest from "@-/com.npmjs.use-latest";
import { boundedPromise, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { describeHanReward } from "@-/dev.improve.haniverse.lib.han-currency";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import {
  type ActiveHanCoinDeposit,
  type HanCoinDepositBalances,
  HanCoinDepositContext,
  type HanCoinDepositContextValue,
  type HanCoinDepositSourceName,
  HanCoinDepositsContext,
  type HanCoinDepositTargetName,
} from "#han-coin-deposit-context";
import type { WindowRect } from "#han-coin-deposit-plan";

/** A layout measurement is a native round trip; past this the deposit uses a fallback place. */
const MEASURE_DEADLINE_MS = 300;
/** Reading the wallet is a local query; past this the counter shows the amount instead. */
const BALANCE_DEADLINE_MS = 5000;

function measureInWindow(view: View | null | undefined): Promise<WindowRect | null> {
  if (view == null) {
    return Promise.resolve(null);
  }
  return boundedPromise<WindowRect | null>(
    { deadline: MEASURE_DEADLINE_MS, operation: "Han coin deposit measure" },
    (resolve) => {
      view.measureInWindow((x, y, width, height) => {
        resolve(width > 0 && height > 0 ? { height, width, x, y } : null);
      });
    },
  ).catch(() => null);
}

/**
 * Owns the Han coin deposits: where coins burst out of (registered sources), where they land
 * (the registered targets: the map sheet's wallet while it is on screen, otherwise the drawer
 * button) and the deposits in flight, which `HanCoinDepositOverlay` draws. Mount it above every
 * screen that earns Han and the targets.
 */
export function HanCoinDepositProvider({
  children,
  isMuted,
  readBalances,
}: {
  children: ReactNode;
  isMuted: boolean;
  /** The wallet's Han and HanMiles right now; the counters count up to them. */
  readBalances: () => Promise<HanCoinDepositBalances>;
}) {
  const sources = useRef(new Map<HanCoinDepositSourceName, View>());
  const targets = useRef(new Map<HanCoinDepositTargetName, View>());
  const nextId = useRef(0);
  const drawerPulse = useSharedValue(0);
  const walletHanPulse = useSharedValue(0);
  const walletHanMilesPulse = useSharedValue(0);
  const targetPulses = useMemo(
    () => ({
      drawer: drawerPulse,
      "wallet-han": walletHanPulse,
      "wallet-han-miles": walletHanMilesPulse,
    }),
    [drawerPulse, walletHanMilesPulse, walletHanPulse],
  );
  const [deposits, setDeposits] = useState<ActiveHanCoinDeposit[]>([]);
  const { height, width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  // Without a mounted drawer button (web), coins land in the top-right corner, where it would be.
  const fallbacks: {
    readonly current: Pick<ActiveHanCoinDeposit, "source" | "target">;
  } = useLatest({
    source: { height: 0, width: 0, x: width / 2, y: height / 2 },
    target: { size: 44, x: width - insets.right - 9 - 22, y: insets.top + 9 + 22 },
  });
  const readBalancesRef: { readonly current: () => Promise<HanCoinDepositBalances> } =
    useLatest(readBalances);

  const deposit = useCallback<HanCoinDepositContextValue["deposit"]>(
    ({ dragons = 0, han = 0, hanMiles = 0, reviewBonusHan = 0, source }) => {
      const reward = { dragons, han, hanMiles, reviewBonusHan };
      if (Object.values(reward).every((amount) => amount <= 0)) {
        return;
      }
      nextId.current += 1;
      const id = nextId.current;
      void (async () => {
        const [sourceRect, walletRect, drawerRect] = await Promise.all([
          measureInWindow(source === null ? null : sources.current.get(source)),
          measureInWindow(targets.current.get("wallet-han")),
          measureInWindow(targets.current.get("drawer")),
        ]);
        // The map sheet's wallet, while it is on screen, is where Han lands; else the drawer.
        const targetRect = walletRect ?? drawerRect;
        setDeposits((current) => [
          ...current,
          {
            ...reward,
            balanceAfter: null,
            id,
            landedHan: 0,
            landsIn: walletRect === null ? "drawer" : "wallet",
            source: sourceRect ?? fallbacks.current.source,
            target:
              targetRect === null
                ? fallbacks.current.target
                : {
                    size: Math.min(targetRect.width, targetRect.height),
                    x: targetRect.x + targetRect.width / 2,
                    y: targetRect.y + targetRect.height / 2,
                  },
          },
        ]);
        AccessibilityInfo.announceForAccessibility(`${describeHanReward(reward)} collected`);
        if (han <= 0 && (walletRect === null || hanMiles <= 0)) {
          // Nothing lands in a counted balance, so the counter shows the reward itself.
          return;
        }
        try {
          const balanceAfter = await withDeadline(
            { deadline: BALANCE_DEADLINE_MS, operation: "Han coin deposit balance" },
            () => readBalancesRef.current(),
          );
          setDeposits((current) =>
            current.map((entry) => (entry.id === id ? { ...entry, balanceAfter } : entry)),
          );
        } catch {
          // The counter shows the amount gained instead of the balance.
        }
      })();
    },
    [fallbacks, readBalancesRef],
  );

  const registerSource = useCallback((name: HanCoinDepositSourceName, view: View) => {
    sources.current.set(name, view);
    return () => {
      if (sources.current.get(name) === view) {
        sources.current.delete(name);
      }
    };
  }, []);
  const registerTarget = useCallback((name: HanCoinDepositTargetName, view: View) => {
    targets.current.set(name, view);
    return () => {
      if (targets.current.get(name) === view) {
        targets.current.delete(name);
      }
    };
  }, []);
  const landHan = useCallback((id: number, han: number) => {
    setDeposits((current) =>
      current.map((entry) =>
        entry.id === id ? { ...entry, landedHan: entry.landedHan + han } : entry,
      ),
    );
  }, []);
  const removeDeposit = useCallback((id: number) => {
    setDeposits((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const contextValue = useMemo(
    () => ({ deposit, registerSource, registerTarget, targetPulses }),
    [deposit, registerSource, registerTarget, targetPulses],
  );
  const depositsValue = useMemo(
    () => ({ deposits, isMuted, landHan, removeDeposit }),
    [deposits, isMuted, landHan, removeDeposit],
  );
  return (
    <HanCoinDepositContext value={contextValue}>
      <HanCoinDepositsContext value={depositsValue}>{children}</HanCoinDepositsContext>
    </HanCoinDepositContext>
  );
}
